import React, { useState, useMemo } from "react";
import Select from "react-select";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import "./SpaccaturaCosti.css";
import { useCollection } from "../../../hooks/useCollection";
import { timestamp } from "../../../firebase/config";
import { startOfMonth, endOfMonth } from "date-fns";

// Helper per calcolare la differenza in ore
const calculateHours = (start, end, pause) => {
  if (!start || !end) return 0;

  try {
    // Funzione interna per ottenere minuti da qualsiasi input
    const getMinutes = (input) => {
      if (!input) return null;

      // Gestione oggetti Date/Timestamp
      let val = input;
      if (typeof val === "object") {
        if (val.toDate) val = val.toDate(); // Firestore Timestamp
        if (val instanceof Date) {
          return val.getHours() * 60 + val.getMinutes();
        }
        // Se è un oggetto strano, prova a stringifarlo o cercare proprietà comuni
        if (val.seconds)
          return (
            new Date(val.seconds * 1000).getHours() * 60 +
            new Date(val.seconds * 1000).getMinutes()
          );
      }

      // Parsing stringa
      const str = String(val).trim();

      // Regex per HH:MM, HH.MM, HH,MM
      const matchTime = str.match(/^(\d{1,2})[:.,](\d{1,2})/);
      if (matchTime) {
        return parseInt(matchTime[1], 10) * 60 + parseInt(matchTime[2], 10);
      }

      // Regex per intero (solo ore)
      const matchInt = str.match(/^(\d{1,2})$/);
      if (matchInt) {
        return parseInt(matchInt[1], 10) * 60;
      }

      return null;
    };

    const startMin = getMinutes(start);
    const endMin = getMinutes(end);

    if (startMin === null || endMin === null) return 0;

    let diff = endMin - startMin;
    // Gestione scavalco mezzanotte
    if (diff < 0) diff += 24 * 60;

    // Gestione pausa
    let pauseMin = 0;
    if (pause) {
      // Rimuovi "min", "ore", ecc per sicurezza
      const cleanPause = String(pause)
        .replace(/[^0-9.,]/g, "")
        .replace(",", ".");
      const pVal = parseFloat(cleanPause);

      if (!isNaN(pVal)) {
        // Standard: input in ore (es. 0.5 = 30min)
        let calculatedPause = pVal * 60;

        // EURISTICA DI SICUREZZA:
        // Se la pausa calcolata è maggiore delle ore lavorate (es. 30 "ore" su 8 ore lavorate),
        // probabilmente l'utente ha inserito minuti (es. 30).
        // Oppure se la pausa è > 4 ore (molto raro).
        if (calculatedPause > diff && calculatedPause > 240) {
          calculatedPause = pVal; // Usa il valore originale come minuti
        }

        pauseMin = calculatedPause;
      }
    }

    const finalHours = Math.max(0, (diff - pauseMin) / 60);

    return finalHours;
  } catch (err) {
    return 0;
  }
};

export default function SpaccaturaCosti() {
  const [selectedMonth, setSelectedMonth] = useState(null);
  const monthRange = useMemo(() => {
    if (!selectedMonth?.value) return null;
    const [year, month] = selectedMonth.value.split("-");
    const start = startOfMonth(new Date(Number(year), Number(month) - 1, 1));
    const end = endOfMonth(start);
    return {
      startTs: timestamp.fromDate(start),
      endTs: timestamp.fromDate(end),
    };
  }, [selectedMonth]);

  const { documents: presenze } = useCollection(
    "presenze",
    monthRange
      ? [
          ["dataRegistrazione", ">=", monthRange.startTs],
          ["dataRegistrazione", "<=", monthRange.endTs],
        ]
      : null,
    ["dataRegistrazione", "asc"],
    undefined,
    false,
  );
  const { documents: users } = useCollection(
    "users",
    null,
    null,
    undefined,
    false,
  );
  const { documents: projects } = useCollection(
    "projects",
    null,
    null,
    undefined,
    false,
  );

  const [analysisMode, setAnalysisMode] = useState("dipendente"); // 'dipendente' or 'mezzo'
  const [selectedResource, setSelectedResource] = useState(null);
  const [totalAmount, setTotalAmount] = useState(""); // Nuovo stato per importo da ripartire

  // --- DOWNLOAD CSV ---
  const downloadCSV = () => {
    if (!selectedMonth || !normalizedData.employees.length) return;

    const monthVal = selectedMonth.value;
    // Filtra solo le presenze del mese selezionato per i dipendenti
    const relevantData = normalizedData.employees.filter(
      (e) => e.month === monthVal,
    );

    if (relevantData.length === 0) {
      alert("Nessun dato da esportare per il mese selezionato.");
      return;
    }

    // Raggruppa per Dipendente -> Cantiere
    // Struttura: { userId: { name:Str, totalHours:Num, projects: { key: { codice, nome, hours } } } }
    const grouped = {};

    relevantData.forEach((item) => {
      const hours = calculateHours(
        item.oraIngresso,
        item.oraUscita,
        item.durataPausa,
      );
      if (hours <= 0) return;

      const uid = item.userId;
      if (!grouped[uid]) {
        // Trova nome dipendente
        const userDoc = users.find((u) => u.id === uid);
        let userName = "SCONOSCIUTO";
        if (userDoc) {
          if (
            userDoc.anagrafica &&
            userDoc.anagrafica.nome &&
            userDoc.anagrafica.cognome
          ) {
            userName =
              `${userDoc.anagrafica.cognome} ${userDoc.anagrafica.nome}`.toUpperCase();
          } else {
            userName = (userDoc.displayName || "SCONOSCIUTO").toUpperCase();
          }
        }

        grouped[uid] = {
          name: userName,
          totalHours: 0,
          projects: {},
        };
      }

      grouped[uid].totalHours += hours;

      const { codice, nome } = item.cantiere;
      const projKey = `${codice}|${nome}`;

      if (!grouped[uid].projects[projKey]) {
        grouped[uid].projects[projKey] = {
          codice,
          nome,
          hours: 0,
        };
      }
      grouped[uid].projects[projKey].hours += hours;
    });

    const csvCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;

    // Genera righe CSV
    // Header: Dipendente, Percentuale, Codice Cantiere
    const csvRows = [["Dipendente", "Percentuale (%)", "Codice Cantiere"]];

    Object.values(grouped).forEach((userStats) => {
      // Se vuoi ordinare per nome cantiere o percentuale, fallo qui
      const projKeys = Object.keys(userStats.projects);

      projKeys.forEach((key) => {
        const proj = userStats.projects[key];
        const percentage =
          userStats.totalHours > 0 ? proj.hours / userStats.totalHours : 0;

        csvRows.push([
          csvCell(userStats.name),
          percentage.toFixed(2).replace(".", ","),
          csvCell(proj.codice),
        ]);
      });
    });

    // Crea contenuto CSV
    const csvContent =
      "data:text/csv;charset=utf-8," +
      csvRows.map((e) => e.join(";")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `SpaccaturaCosti_Dipendenti_${monthVal}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- NORMALIZZAZIONE DATI (RICHIESTA UTENTE) ---
  const normalizedData = useMemo(() => {
    if (!presenze || !projects) return { employees: [], vehicles: [] };

    const employees = [];
    const vehicles = [];

    presenze.forEach((p) => {
      // Controllo preliminare data
      if (!p.dataRegistrazione) return;

      let dateObj;
      if (p.dataRegistrazione.toDate) {
        dateObj = p.dataRegistrazione.toDate();
      } else {
        dateObj = new Date(p.dataRegistrazione);
      }
      if (isNaN(dateObj.getTime())) return;

      const monthStr = format(dateObj, "yyyy-MM");

      // 1. Risoluzione Cantiere
      let cantiereObj = { codice: "N.D.", nome: "N.D." };
      let nomeCantiereRaw = "";

      if (typeof p.cantiere === "object" && p.cantiere !== null) {
        nomeCantiereRaw = p.cantiere.nome;
        if (p.cantiere.codice) cantiereObj.codice = p.cantiere.codice;
        cantiereObj.nome = p.cantiere.nome || "N.D.";
      } else if (typeof p.cantiere === "string") {
        nomeCantiereRaw = p.cantiere;
        cantiereObj.nome = p.cantiere;
      }

      // Match con collection projects
      if (nomeCantiereRaw) {
        const proj = projects.find((x) => x.nome === nomeCantiereRaw);
        if (proj) {
          cantiereObj.codice = proj.codice;
          cantiereObj.nome = proj.nome;
        }
      }

      // 2. Creazione oggetto Dipendente
      if (p.userId) {
        employees.push({
          userId: p.userId,
          oraIngresso: p.oraIngresso,
          oraUscita: p.oraUscita,
          durataPausa: p.durataPausa,
          cantiere: cantiereObj,
          dataRegistrazione: dateObj,
          month: monthStr,
        });
      }

      // 3. Creazione oggetto Mezzo
      if (p.mezzo) {
        let mezzoObj = { id: "N.D.", modello: "N.D.", targa: "N.D." };

        if (typeof p.mezzo === "object" && p.mezzo !== null) {
          mezzoObj = {
            id: p.mezzo.id || "N.D.",
            modello: p.mezzo.modello || "N.D.",
            targa: p.mezzo.targa || "N.D.",
          };
        } else if (typeof p.mezzo === "string") {
          // Parsing stringa "Modello - Targa"
          if (p.mezzo.includes(" - ")) {
            const parts = p.mezzo.split(" - ");
            mezzoObj.modello = parts[0];
            mezzoObj.targa = parts[1];
          } else {
            mezzoObj.targa = p.mezzo;
          }
        }

        vehicles.push({
          mezzo: mezzoObj,
          cantiere: cantiereObj,
          dataRegistrazione: dateObj,
          month: monthStr,
          oraIngresso: p.oraIngresso,
          oraUscita: p.oraUscita,
          durataPausa: p.durataPausa,
        });
      }
    });

    return { employees, vehicles };
  }, [presenze, projects]);

  // --- MONTH OPTIONS ---
  const monthOptions = useMemo(() => {
    // Usiamo i dati normalizzati per estrarre i mesi disponibili
    const sourceData =
      analysisMode === "dipendente"
        ? normalizedData.employees
        : normalizedData.vehicles;

    if (!sourceData.length) return [];

    const uniqueMonths = new Set();
    sourceData.forEach((item) => {
      const label = format(item.dataRegistrazione, "MMMM yyyy", { locale: it });
      const value = item.month;
      const capitalizedLabel = label.charAt(0).toUpperCase() + label.slice(1);
      uniqueMonths.add(JSON.stringify({ value, label: capitalizedLabel }));
    });

    return Array.from(uniqueMonths)
      .map((s) => JSON.parse(s))
      .sort((a, b) => b.value.localeCompare(a.value));
  }, [normalizedData, analysisMode]);

  // --- RESOURCE OPTIONS ---
  const resourceOptions = useMemo(() => {
    if (analysisMode === "dipendente") {
      if (!users) return [];
      // Filtriamo solo gli utenti che hanno effettivamente presenze
      const activeUserIds = new Set(
        normalizedData.employees.map((e) => e.userId),
      );

      return users
        .filter((user) => activeUserIds.has(user.id))
        .map((user) => {
          let label = user.displayName;
          if (
            user.anagrafica &&
            user.anagrafica.nome &&
            user.anagrafica.cognome
          ) {
            label = `${user.anagrafica.nome} ${user.anagrafica.cognome}`;
          }
          return { value: user.id, label };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    } else {
      // Costruiamo la lista dai mezzi normalizzati
      const uniqueVehicles = new Map();

      normalizedData.vehicles.forEach((v) => {
        const key = v.mezzo.targa;
        if (key && key !== "N.D." && !uniqueVehicles.has(key)) {
          let label = v.mezzo.targa;
          if (v.mezzo.modello && v.mezzo.modello !== "N.D.") {
            label = `${v.mezzo.modello} - ${v.mezzo.targa}`;
          }
          uniqueVehicles.set(key, { value: key, label });
        }
      });

      return Array.from(uniqueVehicles.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    }
  }, [analysisMode, users, normalizedData]);

  // --- CALCOLO SPACCATURA ---
  const spaccaturaData = useMemo(() => {
    if (!selectedMonth || !selectedResource) return null;

    let filteredItems = [];

    if (analysisMode === "dipendente") {
      filteredItems = normalizedData.employees.filter((e) => {
        return (
          e.month === selectedMonth.value && e.userId === selectedResource.value
        );
      });
    } else {
      filteredItems = normalizedData.vehicles.filter((v) => {
        return (
          v.month === selectedMonth.value &&
          v.mezzo.targa === selectedResource.value
        );
      });
    }

    const stats = {};
    let totalHours = 0;

    filteredItems.forEach((item) => {
      const hours = calculateHours(
        item.oraIngresso,
        item.oraUscita,
        item.durataPausa,
      );

      if (hours > 0) {
        const { codice, nome } = item.cantiere;
        const key = `${codice}|${nome}`;

        if (!stats[key]) {
          stats[key] = {
            codice: codice,
            cantiere: nome,
            ore: 0,
          };
        }
        stats[key].ore += hours;
        totalHours += hours;
      }
    });

    const result = Object.values(stats)
      .map((item) => ({
        ...item,
        percentuale: totalHours > 0 ? (item.ore / totalHours) * 100 : 0,
      }))
      .sort((a, b) => b.percentuale - a.percentuale);

    return { data: result, totalHours };
  }, [selectedMonth, selectedResource, normalizedData, analysisMode]);

  return (
    <div className="spaccatura-costi-container">
      <div className="header-row">
        <div>
          <h1>Spaccatura Costi</h1>
          <p>Analisi della ripartizione ore per cantiere</p>
        </div>
        <button
          onClick={downloadCSV}
          disabled={!selectedMonth}
          className="csv-btn"
          title="Scarica CSV riepilogo dipendenti"
        >
          <span style={{ marginRight: "8px" }}>📥</span>
          CSV Riepilogo
        </button>
      </div>

      <div className="filters-container">
        <div className="filter-group">
          <label>Mese</label>
          <Select
            options={monthOptions}
            value={selectedMonth}
            onChange={setSelectedMonth}
            placeholder="Seleziona..."
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        <div className="filter-group">
          <label>Modalità</label>
          <div className="mode-switch">
            <button
              className={analysisMode === "dipendente" ? "active" : ""}
              onClick={() => {
                setAnalysisMode("dipendente");
                setSelectedResource(null);
              }}
            >
              Dipendente
            </button>
            <button
              className={analysisMode === "mezzo" ? "active" : ""}
              onClick={() => {
                setAnalysisMode("mezzo");
                setSelectedResource(null);
              }}
            >
              Mezzo
            </button>
          </div>
        </div>

        <div className="filter-group" style={{ flex: 1.5 }}>
          <label>
            {analysisMode === "dipendente" ? "Dipendente" : "Mezzo"}
          </label>
          <Select
            options={resourceOptions}
            value={selectedResource}
            onChange={setSelectedResource}
            placeholder={
              analysisMode === "dipendente"
                ? "Cerca dipendente..."
                : "Cerca mezzo..."
            }
            className="react-select-container"
            classNamePrefix="react-select"
            isDisabled={!selectedMonth}
          />
        </div>

        <div className="filter-group">
          <label>Importo (€)</label>
          <input
            type="number"
            value={totalAmount}
            onChange={(e) => setTotalAmount(e.target.value)}
            placeholder="0.00"
            className="amount-input"
            step="0.01"
          />
        </div>
      </div>

      {spaccaturaData && spaccaturaData.data.length > 0 && (
        <div className="results-container">
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Totale Ore Rilevate</span>
              <span className="stat-value">
                {spaccaturaData.totalHours.toFixed(2)}
                <small>h</small>
              </span>
            </div>
            {totalAmount && !isNaN(totalAmount) && (
              <div className="stat-card highlight">
                <span className="stat-label">Totale Ripartito</span>
                <span className="stat-value">
                  €{" "}
                  {parseFloat(totalAmount).toLocaleString("it-IT", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </div>
            )}
            <div className="stat-card">
              <span className="stat-label">Cantieri Coinvolti</span>
              <span className="stat-value">{spaccaturaData.data.length}</span>
            </div>
          </div>

          <div className="table-wrapper">
            <table className="spaccatura-table">
              <thead>
                <tr>
                  <th>Codice</th>
                  <th>Cantiere</th>
                  <th className="text-right">Ore</th>
                  <th className="text-right">Percentuale</th>
                  <th className="text-right">Ripartizione (€)</th>
                  <th>Incidenza</th>
                </tr>
              </thead>
              <tbody>
                {spaccaturaData.data.map((row, index) => (
                  <tr key={index}>
                    <td className="code-cell">{row.codice}</td>
                    <td className="name-cell">{row.cantiere}</td>
                    <td className="text-right font-mono">
                      {row.ore.toFixed(2)}
                    </td>
                    <td className="text-right font-mono percent-cell">
                      {row.percentuale.toFixed(2)}%
                    </td>
                    <td className="text-right font-mono currency-cell">
                      {totalAmount && !isNaN(totalAmount)
                        ? `€ ${((parseFloat(totalAmount) * row.percentuale) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "-"}
                    </td>
                    <td className="bar-cell">
                      <div className="progress-bar-bg">
                        <div
                          className="progress-bar-fill"
                          style={{ width: `${row.percentuale}%` }}
                        ></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {spaccaturaData &&
        spaccaturaData.data.length === 0 &&
        selectedMonth &&
        selectedResource && (
          <div className="no-data-message">
            Nessuna presenza rilevata per i filtri selezionati.
          </div>
        )}
    </div>
  );
}
